import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { User, AuthTokens } from '@/types'
import { authService } from '@/services/authService'
import { tokenStorage } from '@/utils/tokenStorage'

interface AuthContextType {
  user: User | null
  tokens: AuthTokens | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  register: (userData: any) => Promise<void>
  logout: () => void
  refreshToken: () => Promise<void>
  updateUser: (userData: Partial<User>) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [tokens, setTokens] = useState<AuthTokens | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const isAuthenticated = !!user && !!tokens

  // Initialize auth state from stored tokens
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedTokens = tokenStorage.getTokens()
        if (storedTokens) {
          setTokens(storedTokens)
          
          // Verify token and get user info
          try {
            const userData = await authService.getCurrentUser()
            setUser(userData)
          } catch (error) {
            console.error('Failed to get user data:', error)
            // Clear invalid tokens
            tokenStorage.clearTokens()
          }
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error)
        // Clear invalid tokens
        tokenStorage.clearTokens()
      } finally {
        setIsLoading(false)
      }
    }

    initializeAuth()
  }, [])

  // Set up token refresh interval
  useEffect(() => {
    if (!tokens) return

    const refreshInterval = setInterval(async () => {
      try {
        await refreshToken()
      } catch (error) {
        console.error('Token refresh failed:', error)
        logout()
      }
    }, (tokens.expiresIn - 300) * 1000) // Refresh 5 minutes before expiry

    return () => clearInterval(refreshInterval)
  }, [tokens])

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true)
      const response = await authService.login({ email, password })
      
      setUser(response.user)
      setTokens(response.tokens)
      tokenStorage.setTokens(response.tokens)
    } catch (error) {
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const register = async (userData: any) => {
    try {
      setIsLoading(true)
      const response = await authService.register(userData)
      
      setUser(response.user)
      setTokens(response.tokens)
      tokenStorage.setTokens(response.tokens)
    } catch (error) {
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const logout = () => {
    setUser(null)
    setTokens(null)
    tokenStorage.clearTokens()
    
    // Optional: Call logout endpoint to invalidate token on server
    authService.logout().catch(console.error)
  }

  const refreshToken = async () => {
    try {
      if (!tokens?.refreshToken) {
        throw new Error('No refresh token available')
      }

      const newTokens = await authService.refreshToken(tokens.refreshToken)
      
      setTokens(newTokens)
      tokenStorage.setTokens(newTokens)
    } catch (error) {
      console.error('Token refresh failed:', error)
      logout()
      throw error
    }
  }

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...userData })
    }
  }

  const value: AuthContextType = {
    user,
    tokens,
    isLoading,
    isAuthenticated,
    login,
    register,
    logout,
    refreshToken,
    updateUser,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}